import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  Grid,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
} from '@mui/icons-material';
import { calculateAffordabilityBaseline } from '../../services/api';

const STATES = [
  { code: 'NV', name: 'Nevada', hasTax: false },
  { code: 'CA', name: 'California', hasTax: true },
];

function ScenarioFactors({
  baselineState,
  scenarioState,
  setScenarioState,
  factors,
  setFactors,
  baselineResult,
  incomes,
}) {
  const handleAddFactor = (type) => {
    const newId = Math.max(...factors.map(f => f.id), 0) + 1;
    setFactors([...factors, {
      id: newId,
      factor_type: type,
      label: '',
      monthly_amount: 0,
    }]);
  };

  const handleRemoveFactor = (id) => {
    setFactors(factors.filter(f => f.id !== id));
  };

  const handleFactorChange = (id, field, value) => {
    setFactors(factors.map(f => {
      if (f.id === id) {
        return {
          ...f,
          [field]: field === 'monthly_amount' ? Number(value) || 0 : value
        };
      }
      return f;
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate accurate state change impact via backend
  const [stateChangeImpact, setStateChangeImpact] = React.useState(0);

  React.useEffect(() => {
    if (!baselineResult || baselineState === scenarioState || !incomes?.length) {
      setStateChangeImpact(0);
      return;
    }

    const fetchScenarioBaseline = async () => {
      try {
        const response = await calculateAffordabilityBaseline({
          state: scenarioState,
          incomes: incomes.map(inc => ({
            label: inc.label,
            gross_annual: inc.gross_annual,
            type: inc.type,
          })),
          pay_interval: incomes[0]?.frequency || 'bi-weekly',
        });
        const scenarioNetMonthly = response.data.net_monthly || 0;
        const baselineNetMonthly = baselineResult.net_monthly || 0;
        setStateChangeImpact(Math.round(scenarioNetMonthly - baselineNetMonthly));
      } catch (err) {
        console.error('Failed to calculate state change impact:', err);
        setStateChangeImpact(0);
      }
    };

    fetchScenarioBaseline();
  }, [baselineResult, baselineState, scenarioState, incomes]);

  // Calculate factor totals
  const incomeFactors = factors.filter(f => f.factor_type === 'income');
  const expenseFactors = factors.filter(f => f.factor_type === 'expense');
  const totalIncome = incomeFactors.reduce((sum, f) => sum + f.monthly_amount, 0);
  const totalExpense = expenseFactors.reduce((sum, f) => sum + f.monthly_amount, 0);
  const netFactorsImpact = totalIncome - totalExpense;
  const totalImpact = stateChangeImpact + netFactorsImpact;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Define Your Scenario
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Change the state or add income/expense factors to see how they impact your allocations.
      </Typography>

      {/* State Selection */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth>
              <InputLabel>Scenario State</InputLabel>
              <Select
                value={scenarioState}
                label="Scenario State"
                onChange={(e) => setScenarioState(e.target.value)}
              >
                {STATES.map(s => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name} {s.hasTax ? '(has state tax)' : '(no state tax)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            {baselineState !== scenarioState ? (
              <Chip
                label={`State change: ${formatCurrency(stateChangeImpact)}/mo`}
                color={stateChangeImpact >= 0 ? 'success' : 'error'}
                variant="outlined"
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Same as current state ({baselineState})
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Add Factor Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Additional Factors
        </Typography>
        <IconButton
          onClick={() => handleAddFactor('income')}
          color="success"
          size="small"
          title="Add Income"
        >
          <AddIcon />
          <IncomeIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => handleAddFactor('expense')}
          color="error"
          size="small"
          title="Add Expense"
        >
          <AddIcon />
          <ExpenseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Factors List */}
      {factors.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <Typography color="text.secondary">
            No additional factors. Click + to add income or expense changes.
          </Typography>
        </Paper>
      ) : (
        factors.map(factor => (
          <Paper
            key={factor.id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderLeft: '4px solid',
              borderLeftColor: factor.factor_type === 'income' ? 'success.main' : 'error.main',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <Chip
                  icon={factor.factor_type === 'income' ? <IncomeIcon /> : <ExpenseIcon />}
                  label={factor.factor_type === 'income' ? 'Income' : 'Expense'}
                  color={factor.factor_type === 'income' ? 'success' : 'error'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={factor.label}
                  onChange={(e) => handleFactorChange(factor.id, 'label', e.target.value)}
                  placeholder={factor.factor_type === 'income' ? 'e.g., Side gig' : 'e.g., Rent increase'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monthly Amount"
                  type="number"
                  value={factor.monthly_amount || ''}
                  onChange={(e) => handleFactorChange(factor.id, 'monthly_amount', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton
                  onClick={() => handleRemoveFactor(factor.id)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))
      )}

      {/* Impact Summary */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: totalImpact >= 0 ? 'success.light' : 'error.light' }}>
        <Typography variant="h6" gutterBottom>
          Net Scenario Impact
        </Typography>
        <Grid container spacing={2}>
          {baselineState !== scenarioState && (
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">State Change</Typography>
              <Typography variant="h6" color={stateChangeImpact >= 0 ? 'success.dark' : 'error.dark'}>
                {stateChangeImpact >= 0 ? '+' : ''}{formatCurrency(stateChangeImpact)}/mo
              </Typography>
            </Grid>
          )}
          {incomeFactors.length > 0 && (
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Income Factors</Typography>
              <Typography variant="h6" color="success.dark">
                +{formatCurrency(totalIncome)}/mo
              </Typography>
            </Grid>
          )}
          {expenseFactors.length > 0 && (
            <Grid item xs={6} sm={3}>
              <Typography variant="body2">Expense Factors</Typography>
              <Typography variant="h6" color="error.dark">
                -{formatCurrency(totalExpense)}/mo
              </Typography>
            </Grid>
          )}
          <Grid item xs={6} sm={3}>
            <Typography variant="body2"><strong>Total Impact</strong></Typography>
            <Typography variant="h6">
              {totalImpact >= 0 ? '+' : ''}{formatCurrency(totalImpact)}/mo
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default ScenarioFactors;
